import { FlagIcon } from "@xola/icons";
import { getCustomerName, isMerchandiseItem, isMistPurchase } from "common";
import React, { useEffect, useState } from "react";
import { Guest, Organizer, Purchase, PurchaseItem } from "types";
import { MessageDrawer } from "../Common/MessageModal/MessageDrawer";
import { MessageSendTo } from "../Common/MessageModal/messageDrawerHelpers";
import { TravelerMessage } from "../Common/MessageModal/TravelersSelector";
import { NoTranslate } from "../Common/NoTranslate";
import { useSourceMessagingPlugin } from "../Common/SourceIcon";
import { PurchaseItemSelect } from "./PurchaseItemSelect";
import { PurchaseSummaryTile } from "./PurchaseSummaryTile";

interface MessagePurchaseModalProps {
    purchase: Purchase;
    item?: PurchaseItem;
    description?: string;
    isOpen: boolean;
    isTravelersSelectable?: boolean;
    isOrganizerOnly?: boolean;
    to?: MessageSendTo[];
    preferredChannels?: string[];
    onClose: () => void;
    onContinue?: () => void;
}

export const MessagePurchaseModal = ({
    purchase,
    item,
    description,
    isOpen,
    isTravelersSelectable = false,
    isOrganizerOnly = false,
    to,
    preferredChannels,
    onClose,
    onContinue,
}: MessagePurchaseModalProps) => {
    const [selectedItem, setSelectedItem] = useState<PurchaseItem | undefined>();
    const messagingPlugin = useSourceMessagingPlugin(purchase.source);

    useEffect(() => {
        if (item && isOpen) {
            setSelectedItem(undefined);
        }
    }, [item, isOpen]);

    // Mount the drawer shell whenever `item` exists so the slide-in transition has something to animate
    // against; `MessageDrawer` gates `MessageDrawerContent` on `isOpen` so Formik + template fetch only run
    // once the drawer is opened.
    if (!item) {
        return null;
    }

    const activeItem = selectedItem ?? item;

    const allTravelersWithInfo = [
        activeItem.organizer,
        ...(activeItem.guests?.filter(
            (guest) => guest?.traveler && guest?.traveler?.id !== activeItem.organizer?.user?.id,
        ) ?? []),
    ];

    const travelerListForSelecting = allTravelersWithInfo.map<TravelerMessage>((guest) => ({
        id: (guest as Guest)?.traveler?.id ?? (guest as Organizer)?.user?.id ?? "",
        name: guest?.name ?? "",
        type: "traveler",
    }));

    const defaultTravelerList = isOrganizerOnly
        ? [{ id: purchase.organizer?.id, type: "traveler" as const }]
        : purchase?.travelers?.map<{ id?: ID; type: "traveler" }>((traveler) => ({
              id: traveler.id,
              type: "traveler",
          }));

    const isMerchandise = isMerchandiseItem(activeItem);

    return (
        <MessageDrawer
            filters={{ purchase: { id: purchase.id }, item: activeItem.id }}
            travelersList={isTravelersSelectable ? travelerListForSelecting : undefined}
            to={to ?? (isTravelersSelectable ? undefined : defaultTravelerList)}
            channels={{
                isEmailAvailable: !!purchase.customerEmail,
                isPhoneAvailable: !!purchase.phone,
                messagingPlugin: messagingPlugin ?? undefined,
                preferred: preferredChannels,
            }}
            description={isTravelersSelectable ? undefined : description}
            isOpen={isOpen}
            shouldShowEmailGuides={!isMerchandise}
            onClose={onClose}
            onContinue={onContinue}
        >
            <ModalContent
                purchase={purchase}
                item={activeItem}
                showItemSelector={isMistPurchase(purchase)}
                onItemChange={setSelectedItem}
            />
        </MessageDrawer>
    );
};

interface ModalContentProps {
    purchase: Purchase;
    item: PurchaseItem;
    showItemSelector: boolean;
    onItemChange: (item: PurchaseItem) => void;
}

const ModalContent = ({ purchase, item, showItemSelector, onItemChange }: ModalContentProps) => {
    return (
        <div>
            {showItemSelector ? (
                <div className="ui-purchase-summary-tile rounded-lg ">
                    <div className="w-full py-1 font-bold">
                        <FlagIcon size="medium" className="mr-1 mb-1 text-warning" />
                        <NoTranslate as="span">{getCustomerName(purchase)}</NoTranslate>
                    </div>
                    <div>
                        <PurchaseItemSelect
                            items={purchase.items}
                            value={item}
                            className="flex-1"
                            onChange={onItemChange}
                        />
                    </div>
                </div>
            ) : (
                <PurchaseSummaryTile showGuests purchase={purchase} itemId={item.id} />
            )}
        </div>
    );
};
